import React, { useState } from "react";
import { getSidebarMenuConfig } from "./config/menu.config";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SidebarLogo from "./SidebarLogo";
import SidebarMenuItem from "./components/SidebarMenuItem";
import { usePermissions } from "@hooks/usePermissions";
import type { Permission } from "@constants/user.roles";
import "./Sidebar.css";
import { openItemOnSideBarClick } from "./utils/openItemOnSideBarClick";

// Menu item type
interface MenuItem {
	key: string;
	label: string;
	icon: React.ReactNode;
	route: string;
	children?: MenuItem[];
	requiredPermission?: string;
}

const Sidebar: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation();
	// const { openModal } = useModal();
	const { hasPermission, user } = usePermissions();
	// const userEmail = useSelector((state: RootState) => state.user.user?.emailAddress);
	const menu = getSidebarMenuConfig(t);
	const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

	const toggleMenu = (menuKey: string) => {
		setExpandedMenus(prev => (prev.includes(menuKey) ? prev.filter(key => key !== menuKey) : [...prev, menuKey]));
	};

	const isMenuExpanded = (menuKey: string) => expandedMenus.includes(menuKey);
	const isActiveRoute = (route: string) => {
		// Special handling for Course Builder routes
		if (route === "/course-builder") {
			return location.pathname.startsWith("/course-builder");
		}
		return location.pathname === route;
	};
	const isChildActive = (children: MenuItem[]) => {
		return children?.some(child => isActiveRoute(child.route));
	};

	/**
	 * Filter menu items based on user permissions
	 * More permissive filtering to ensure menu visibility, but filter My Committee specifically
	 */
	const filterMenuByPermissions = (items: MenuItem[]): MenuItem[] => {
		return items.filter(item => {
			// If no permission required, show the item
			if (!item.requiredPermission) {
				return true;
			}

			// Check if user has the required permission
			const hasRequiredPermission = hasPermission(item.requiredPermission as Permission);

			// If item has children, filter them too
			if (item.children) {
				const filteredChildren = filterMenuByPermissions(item.children);
				// Show parent if user has permission OR if any children are visible OR if user is authenticated
				const shouldShowParent = hasRequiredPermission || filteredChildren.length > 0 || !!user;
				// if (item.key === COMMITTEES_MENU_KEY) {
				// }
				// Update the children array to only show filtered children
				item.children = filteredChildren;
				return shouldShowParent;
			}

			// For items without children (child menu items), show if user has permission OR if user is authenticated
			// EXCEPT for My Committee, Requests, Audit Log, and Reports which should be strictly filtered
			return hasRequiredPermission || !!user;
		});
	};

	const renderMenuItems = (items: MenuItem[], _parentKey: string = "", level: number = 0) =>
		items.map(item => {
			const hasChildren = Boolean(item.children && item.children.length > 0);
			const isExpanded = isMenuExpanded(item.key);
			const isActive = isActiveRoute(item.route);
			const hasActiveChild = hasChildren && isChildActive(item.children || []);
			return (
				<SidebarMenuItem
					key={item.key}
					item={item}
					level={level}
					isExpanded={isExpanded}
					isActive={isActive}
					hasActiveChild={hasActiveChild}
					onClick={() => openItemOnSideBarClick(item, hasChildren, toggleMenu, navigate)}
					toggleMenu={toggleMenu}
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					renderChildren={(children, _unusedParentKey) => renderMenuItems(children || [], item.key, level + 1)}
				/>
			);
		});

	return (
		<aside className="sidebar-bg w-72 h-screen flex flex-col items-center border-r fixed left-0 top-0 z-20">
			<SidebarLogo />
			<nav className="w-full flex-1 overflow-y-auto">
				<ul className="space-y-4 px-4 w-full mb-6">{renderMenuItems(menu)}</ul>
			</nav>
		</aside>
	);
};

export default Sidebar;
